import { Box, Tooltip, Typography, Divider } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { keyframes } from '@mui/system';
import healthService from '../../services/healthService';
import type { SystemHealthResponse, ServiceHealth } from '../../services/healthService';

const pulseAnimation = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.4; }
  100% { opacity: 1; }
`;

const getStatusColor = (status: string): string => {
  switch (status) {
    case 'healthy':
      return '#4caf50'; // green
    case 'degraded':
      return '#ff9800'; // yellow/orange
    case 'down':
    case 'unhealthy':
      return '#f44336'; // red
    default:
      return '#9e9e9e'; // gray
  }
};

const getStatusLabel = (status: string): string => {
  switch (status) {
    case 'healthy':
      return 'Operativo';
    case 'degraded':
      return 'Degradato';
    case 'down':
    case 'unhealthy':
      return 'Non disponibile';
    default:
      return 'Sconosciuto';
  }
};

const getStatusIcon = (status: string): string => {
  switch (status) {
    case 'healthy':
      return '✅';
    case 'degraded':
      return '⚠️';
    case 'down':
    case 'unhealthy':
      return '❌';
    default:
      return '❓';
  }
};

const getServiceDisplayName = (key: string): string => {
  const names: Record<string, string> = {
    mongodb: 'MongoDB',
    redis: 'Redis',
    email: 'Email',
    queue: 'Queue',
    scheduler: 'Scheduler'
  };
  return names[key] || key;
};

const getServiceStatusText = (service: ServiceHealth): string => {
  if (service.responseTime !== undefined) {
    return `${service.responseTime}ms`;
  }
  if (service.message) {
    return service.message;
  }
  return getStatusLabel(service.status);
};

interface TooltipContentProps {
  data: SystemHealthResponse | undefined;
  isLoading: boolean;
  isError: boolean;
}

const TooltipContent: React.FC<TooltipContentProps> = ({ data, isLoading, isError }) => {
  if (isLoading) {
    return (
      <Box sx={{ p: 1 }}>
        <Typography variant="body2">Caricamento...</Typography>
      </Box>
    );
  }

  if (isError || !data) {
    return (
      <Box sx={{ p: 1 }}>
        <Typography variant="body2" color="error">
          Impossibile verificare lo stato del sistema
        </Typography>
      </Box>
    );
  }

  const formatTime = (timestamp: string): string => {
    try {
      return new Date(timestamp).toLocaleTimeString('it-IT');
    } catch {
      return 'N/A';
    }
  };

  const serviceOrder: (keyof SystemHealthResponse['services'])[] = [
    'mongodb',
    'redis',
    'email',
    'queue',
    'scheduler'
  ];

  return (
    <Box sx={{ p: 1, minWidth: 200 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Stato Sistema: {getStatusIcon(data.status)} {getStatusLabel(data.status)}
      </Typography>

      <Divider sx={{ my: 1 }} />

      {serviceOrder.map((key) => {
        const service = data.services[key];
        if (!service) return null;

        return (
          <Box
            key={key}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              py: 0.5
            }}
          >
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>{service.semaphore}</span>
              <span>{getServiceDisplayName(key)}</span>
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: service.status === 'healthy' ? 'text.secondary' : 'error.main',
                fontFamily: 'monospace',
                fontSize: '0.75rem'
              }}
            >
              {getServiceStatusText(service)}
            </Typography>
          </Box>
        );
      })}

      <Divider sx={{ my: 1 }} />

      <Typography variant="caption" color="text.secondary">
        Ultimo check: {formatTime(data.timestamp)}
      </Typography>
    </Box>
  );
};

const HealthIndicator: React.FC = () => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['systemHealth'],
    queryFn: healthService.getSystemHealth,
    refetchInterval: 60000, // 1 minute
    staleTime: 30000, // 30 seconds
    retry: 2
  });

  const getIndicatorColor = (): string => {
    if (isLoading) return '#9e9e9e'; // gray
    if (isError || !data) return '#f44336'; // red
    return getStatusColor(data.status);
  };

  const shouldPulse = isError || data?.status === 'down';

  return (
    <Tooltip
      title={<TooltipContent data={data} isLoading={isLoading} isError={isError} />}
      arrow
      placement="bottom"
    >
      <Box
        sx={{
          width: 12,
          height: 12,
          borderRadius: '50%',
          backgroundColor: getIndicatorColor(),
          border: '2px solid rgba(255, 255, 255, 0.8)',
          boxShadow: `0 0 4px ${getIndicatorColor()}`,
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          ...(shouldPulse && {
            animation: `${pulseAnimation} 1.5s ease-in-out infinite`
          }),
          '&:hover': {
            transform: 'scale(1.2)',
            boxShadow: `0 0 8px ${getIndicatorColor()}`
          }
        }}
        aria-label={`Stato sistema: ${data ? getStatusLabel(data.status) : 'sconosciuto'}`}
      />
    </Tooltip>
  );
};

export default HealthIndicator;
